import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { FamilyType } from "@/features/Login/Family/types.ts";
import { useRegenerateInviteCode } from "@/hooks/familyHooks";

interface InviteManagerProps {
  family: FamilyType;
  isOwner: boolean;
}

const InviteManager: React.FC<InviteManagerProps> = ({ family, isOwner }) => {
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  const regenerateCodeMutation = useRegenerateInviteCode();

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(family.inviteCode);
      setCopiedToClipboard(true);

      // Masquer le message après 3 secondes
      setTimeout(() => {
        setCopiedToClipboard(false);
      }, 3000);
    } catch (error) {
      console.error("Impossible de copier le code", error);
    }
  };

  const handleRegenerateCode = async () => {
    try {
      await regenerateCodeMutation.mutateAsync();
    } catch (error) {
      console.error("Erreur lors de la régénération du code", error);
    }
  };

  const toggleQRCode = () => {
    setShowQRCode(!showQRCode);
  };

  // Fonction pour générer visuellement le code QR (à titre d'exemple)
  const renderQRCode = () => {
    // Ceci est un exemple simple
    // Dans une application réelle, vous pourriez utiliser une bibliothèque comme qrcode.react
    return (
      <Box
        sx={{
          width: 200,
          height: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          mx: "auto",
          mb: 2,
          background: `repeating-linear-gradient(
            45deg,
            #f0f0f0,
            #f0f0f0 10px,
            #f9f9f9 10px,
            #f9f9f9 20px
          )`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Code QR pour l'invitation
          <br />
          {family.inviteCode}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Typography variant="h6" component="h2" gutterBottom>
        Code d'invitation
      </Typography>

      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Partagez ce code avec les personnes que vous souhaitez inviter à
            rejoindre votre famille :
          </Typography>

          <Box mt={2} mb={3}>
            <TextField
              fullWidth
              value={family.inviteCode}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copier le code">
                    <IconButton edge="end" onClick={handleCopyToClipboard}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  letterSpacing: "0.1rem",
                },
              }}
            />
          </Box>

          {showQRCode && renderQRCode()}

          <Box display="flex" flexWrap="wrap" gap={1}>
            <Button
              variant="outlined"
              startIcon={<QrCodeIcon />}
              onClick={toggleQRCode}
              size="small"
            >
              {showQRCode ? "Masquer le QR Code" : "Afficher le QR Code"}
            </Button>

            {isOwner && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={
                  regenerateCodeMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={handleRegenerateCode}
                disabled={regenerateCodeMutation.isPending}
                size="small"
              >
                Générer un nouveau code
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" component="h2" gutterBottom>
        Instructions
      </Typography>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="body1" paragraph>
          Pour inviter un nouveau membre à rejoindre votre famille :
        </Typography>

        <ol style={{ paddingLeft: "1.5rem" }}>
          <li>
            <Typography variant="body2" paragraph>
              Copiez le code d'invitation ci-dessus en cliquant sur l'icône de
              copie.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" paragraph>
              Partagez ce code avec la personne que vous souhaitez inviter.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              La personne invitée doit se rendre sur la page "Famille" et
              utiliser ce code dans la section "Rejoindre une famille".
            </Typography>
          </li>
        </ol>
      </Paper>

      {isOwner && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Si vous générez un nouveau code d'invitation, l'ancien code ne
          fonctionnera plus. Les membres actuels de la famille ne seront pas
          affectés.
        </Alert>
      )}

      <Snackbar
        open={copiedToClipboard}
        autoHideDuration={3000}
        onClose={() => setCopiedToClipboard(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled">
          Code d'invitation copié dans le presse-papiers !
        </Alert>
      </Snackbar>
    </>
  );
};

export default InviteManager;
